// Mirror of lambda/lib/searchUtils.js — kept in sync so client-side preload
// search behaves identically to the server-side ILIKE path.

const TRANSLATION_MAP = {
  // Proteins
  pollo: 'chicken', chicken: 'pollo',
  carne: 'beef', beef: 'carne',
  cerdo: 'pork', pork: 'cerdo',
  pescado: 'fish', fish: 'pescado',
  camarones: 'shrimp', shrimp: 'camarones',
  // Dairy
  leche: 'milk', milk: 'leche',
  queso: 'cheese', cheese: 'queso',
  mantequilla: 'butter', butter: 'mantequilla',
  yogur: 'yogurt', yogurt: 'yogur',
  crema: 'cream', cream: 'crema',
  huevos: 'eggs', eggs: 'huevos',
  huevo: 'egg', egg: 'huevo',
  // Grains & Staples
  arroz: 'rice', rice: 'arroz',
  pan: 'bread', bread: 'pan',
  harina: 'flour', flour: 'harina',
  pasta: 'pasta',
  azucar: 'sugar', sugar: 'azucar',
  sal: 'salt', salt: 'sal',
  aceite: 'oil', oil: 'aceite',
  // Produce
  manzana: 'apple', apple: 'manzana',
  platano: 'banana', banana: 'platano',
  tomate: 'tomato', tomato: 'tomate',
  cebolla: 'onion', onion: 'cebolla',
  ajo: 'garlic', garlic: 'ajo',
  papa: 'potato', potato: 'papa',
  lechuga: 'lettuce', lettuce: 'lechuga',
  zanahoria: 'carrot', carrot: 'zanahoria',
  limon: 'lemon', lemon: 'limon',
  naranja: 'orange', orange: 'naranja',
  aguacate: 'avocado', avocado: 'aguacate',
  // Beverages
  agua: 'water', water: 'agua',
  jugo: 'juice', juice: 'jugo',
  cerveza: 'beer', beer: 'cerveza',
  cafe: 'coffee', coffee: 'cafe',
  te: 'tea', tea: 'te',
  refresco: 'soda', soda: 'refresco',
  // Household / General
  jabon: 'soap', soap: 'jabon',
  papel: 'paper', paper: 'papel',
  detergente: 'detergent', detergent: 'detergente',
  grande: 'large', large: 'grande',
  pequeno: 'small', small: 'pequeno',
  blanco: 'white', white: 'blanco',
  negro: 'black', black: 'negro',
  rojo: 'red', red: 'rojo',
  verde: 'green', green: 'verde',
  amarillo: 'yellow', yellow: 'amarillo',
  // Snacks
  galletas: 'cookies', cookies: 'galletas',
  chocolate: 'chocolate',
  dulce: 'candy', candy: 'dulce',
  chips: 'chips',
  // Canned / Packaged
  frijoles: 'beans', beans: 'frijoles',
  habichuelas: 'beans',
  sopa: 'soup', soup: 'sopa',
  atun: 'tuna', tuna: 'atun',
  salsa: 'sauce', sauce: 'salsa',
  // Frozen
  helado: 'ice cream',
  congelado: 'frozen', frozen: 'congelado',
};

const ACCENT_RE = /[áàäâéèëêíìïîóòöôúùüûñ]/g;
const ACCENT_MAP = {
  'á': 'a', 'à': 'a', 'ä': 'a', 'â': 'a',
  'é': 'e', 'è': 'e', 'ë': 'e', 'ê': 'e',
  'í': 'i', 'ì': 'i', 'ï': 'i', 'î': 'i',
  'ó': 'o', 'ò': 'o', 'ö': 'o', 'ô': 'o',
  'ú': 'u', 'ù': 'u', 'ü': 'u', 'û': 'u',
  'ñ': 'n',
};

export function normalize(str) {
  if (!str) return '';
  return String(str).toLowerCase().replace(ACCENT_RE, (ch) => ACCENT_MAP[ch] || ch).trim();
}

/**
 * "cebolla amarilla" -> [["cebolla","onion"], ["amarilla","yellow"]]
 * AND between groups, OR within group.
 */
export function expandSearchTerms(rawSearch) {
  const normalized = normalize(rawSearch);
  if (!normalized) return [];
  const words = normalized.split(/\s+/).filter((w) => w.length > 0);
  const groups = [];
  for (const word of words) {
    const group = [word];
    const translation = TRANSLATION_MAP[word];
    if (translation) group.push(normalize(translation));
    groups.push(group);
  }
  return groups;
}

const SEARCH_FIELDS = ['upc', 'brand', 'description', 'category'];

/**
 * Returns true iff every group has at least one term that hits any of the
 * 4 normalized fields on the row (substring match). Mirrors the server's
 * AND-of-groups / OR-within-group SQL clause.
 */
export function matchesAllGroups(row, termGroups) {
  const fields = SEARCH_FIELDS.map((f) => normalize(row[f]));
  for (const group of termGroups) {
    let groupHit = false;
    for (const term of group) {
      for (const fieldVal of fields) {
        if (fieldVal && fieldVal.includes(term)) { groupHit = true; break; }
      }
      if (groupHit) break;
    }
    if (!groupHit) return false;
  }
  return true;
}

/**
 * Rank tiers (lower = better): 0 exact UPC, 1 exact brand, 2 exact description,
 * 3 exact category, 4 partial (ILIKE-style hit only). Matches lambda/lib/searchUtils.js.
 */
export function rankProduct(row, termGroups) {
  const upc = normalize(row.upc);
  const brand = normalize(row.brand);
  const description = normalize(row.description);
  const category = normalize(row.category);
  const allTerms = termGroups.flat();
  for (const term of allTerms) {
    if (upc === term) return 0;
    if (brand === term) return 1;
    if (description === term) return 2;
    if (category === term) return 3;
  }
  return 4;
}
